import React from 'react';

import { Route } from '@lumapps/router';

export interface SideNavRoute {
    route: Route;
    pathMatchForHighlighting: string;
    label: string;
    icon?: string;
    id?: string;
    pageKeys?: string[];
    /** Intermediate ancestor labels between the registering menu and this route (excludes the registering menu itself) */
    parentLabels: string[];
}

/**
 * Recursively traverses a JSX children tree and extracts all navigation routes,
 * regardless of how deeply they are nested.
 *
 * This is used by SideNavigationMenu to pre-register child routes with Spotlight
 * even when the menu is closed (and its children are therefore unmounted by the DS).
 * Without this traversal, only the routes of mounted components would be indexed.
 *
 * The function works by reading props directly from JSX elements — no component
 * needs to mount for its route to be discovered.
 *
 * Hierarchy rules:
 * - A child WITH a `route` prop is a leaf navigation item. It is added to the result
 *   with the accumulated `ancestorLabels` collected along the way.
 * - A child WITHOUT a `route` prop but WITH `children` is an intermediate sub-menu
 *   (SideNavigationItem always renders as either a link OR a menu, never both).
 *   Its `label` is appended to `ancestorLabels` before recursing into its children,
 *   so that leaf items deeper in the tree carry the full ancestor chain.
 *
 * The `parentLabels` field on each returned SideNavRoute contains only the
 * intermediate ancestors between the calling SideNavigationMenu and the route.
 * The calling menu's own label is NOT included — it is prepended by the caller
 * (SideNavigationMenu) when building the final NavigationPage for Spotlight.
 *
 * @example
 * Given the following JSX passed as `children` to a "Directories" SideNavigationMenu:
 *   <SideNavigationItem label="All" route={directoriesRoute} />
 *   <SideNavigationItem label="Electronics">  // no route → intermediate sub-menu
 *     <SideNavigationItem label="Contents" route={entriesRoute} />
 *     <SideNavigationItem label="Tags"     route={tagsRoute} />
 *   </SideNavigationItem>
 *
 * extractRoutes(children) returns:
 * [
 *   { label: 'All',      route: directoriesRoute, parentLabels: [] },
 *   { label: 'Contents', route: entriesRoute,     parentLabels: ['Electronics'] },
 *   { label: 'Tags',     route: tagsRoute,        parentLabels: ['Electronics'] },
 * ]
 *
 * SideNavigationMenu then registers each item by prepending its own label:
 *   "Contents" → parentLabels: ['Directories', 'Electronics']
 *   → breadcrumb: Directories > Electronics > Contents
 */
export const extractRoutes = (children: React.ReactNode, ancestorLabels: string[] = []): SideNavRoute[] => {
    const allRoutes: SideNavRoute[] = [];

    React.Children.forEach(children, (child) => {
        if (React.isValidElement(child)) {
            const {
                route,
                pathMatchForHighlighting,
                label,
                icon,
                id,
                children: grandChildren,
                pageKeys,
            } = child.props as any;

            if (route) {
                allRoutes.push({
                    route,
                    pathMatchForHighlighting,
                    label,
                    icon,
                    id,
                    pageKeys,
                    parentLabels: ancestorLabels,
                });
            }

            if (grandChildren) {
                // Non-route children are intermediate sub-menus: accumulate their label before recursing
                const nextAncestorLabels = route ? ancestorLabels : [...ancestorLabels, ...(label ? [label] : [])];
                allRoutes.push(...extractRoutes(grandChildren, nextAncestorLabels));
            }
        }
    });

    return allRoutes;
};
